// src/pages/profile/Profile.js
import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import api from "../../api/axios";
import { EditSalesUserSchema } from "../../utils/editUserSchema";
import { useTranslation } from "react-i18next";
import { ThemeContext } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import buildSelectedLocations from "../../utils/buildSelectedLocations";
import FloatingInput from "../../components/input/FloatingInput";
import CustomSelect from "../../components/customselect/CustomSelect";
import Toast from "../../components/common/Toast";
import profilepics from "../../images/profile-pics.png";
import "./sprofile.css";
import { useNavigate } from "react-router-dom";
import { buildCompanyPath } from "../../utils/landingCompany";

export default function SalesProfile() {
  const { t: trans } = useTranslation();
  const theme = useContext(ThemeContext);
  const { user, logoutUser, updateUser } = useAuth();
  const navigate = useNavigate();
  const [savedCountyMap, setSavedCountyMap] = useState({});
  const [addressCounties, setAddressCounties] = useState([]);
  const [states, setStates] = useState([]);
  const [counties, setCounties] = useState([]);
  const [titleReps, setTitleReps] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isProfileLoaded, setIsProfileLoaded] = useState(false);
  const [modalStateIds, setModalStateIds] = useState([]);
  const [modalCountiesMap, setModalCountiesMap] = useState({});
  const [modalSelectedCounties, setModalSelectedCounties] = useState({});
  const [profilePic, setProfilePic] = useState(profilepics);
  const [previewPic, setPreviewPic] = useState(null);
  const [passwords, setPasswords] = useState({
    password: "",
    confirmPassword: "",
  });

  // Track if we're in submit process
  const isSubmittingRef = useRef(false);

  // Initialize localUser with empty values
  const [localUser, setLocalUser] = useState({
    first_name: "",
    last_name: "",
    email: "",
    mailing_address: "",
    city: "",
    state: "",
    county: "",
    postal_code: "",
    phone: "",
    titlerep: "",
    title: "",
    address1: "",
    address2: "",
    address_city: "",
    address_state: "",
    address_county: "",
    address_postal_code: "",
  });

  /** ✅ Validation schema */
  const schema = useMemo(
    () => EditSalesUserSchema(states, counties, titleReps, trans),
    [states, counties, titleReps, trans]
  );
  const ucWords = (str = "") =>
    str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

  const {
    register,
    handleSubmit,
    control,
    setError,
    reset,
    setValue,
    watch,
    clearErrors,
    getValues,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: localUser,
  });

  const zipControllerRef = useRef(null);
  const addressStateValue = watch("address_state");
  const stateFieldValue = watch("state");
  const hasInitializedStateRef = useRef(false);
  const hasInitializedAddressStateRef = useRef(false);
  const [selectedCountyId, setSelectedCountyId] = useState(null);

  const [toaster, setToaster] = useState({
    message: "",
    type: "success",
    visible: false,
  });

  // Function to format phone for display based on length - SAME AS Profile.js
  const formatPhoneForDisplay = (digits) => {
    if (!digits) return "";

    if (digits.length <= 10) {
      // Format as XXX-XXX-XXXX for 10 digits
      if (digits.length <= 3) {
        return digits;
      } else if (digits.length <= 6) {
        return `${digits.slice(0, 3)}-${digits.slice(3)}`;
      } else {
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(
          6,
          10
        )}`;
      }
    } else if (digits.length === 11) {
      // Format as X XXX-XXX-XXXX for 11 digits
      if (digits.length <= 1) {
        return digits;
      } else if (digits.length <= 4) {
        return `${digits.slice(0, 1)} ${digits.slice(1)}`;
      } else if (digits.length <= 7) {
        return `${digits.slice(0, 1)} ${digits.slice(1, 4)}-${digits.slice(4)}`;
      } else {
        return `${digits.slice(0, 1)} ${digits.slice(1, 4)}-${digits.slice(
          4,
          7
        )}-${digits.slice(7, 11)}`;
      }
    } else if (digits.length === 12) {
      // Format as XX XXX-XXX-XXXX for 12 digits
      if (digits.length <= 2) {
        return digits;
      } else if (digits.length <= 5) {
        return `${digits.slice(0, 2)} ${digits.slice(2)}`;
      } else if (digits.length <= 8) {
        return `${digits.slice(0, 2)} ${digits.slice(2, 5)}-${digits.slice(5)}`;
      } else {
        return `${digits.slice(0, 2)} ${digits.slice(2, 5)}-${digits.slice(
          5,
          8
        )}-${digits.slice(8, 12)}`;
      }
    }

    // For any other length, return digits as is
    return digits;
  };

  // Function to format phone as user types - SAME AS Profile.js
  const formatPhoneInput = (value) => {
    const digits = value.replace(/\D/g, "");

    if (digits.length <= 10) {
      // Format as XXX-XXX-XXXX for 10 digits
      if (digits.length <= 3) {
        return digits;
      } else if (digits.length <= 6) {
        return `${digits.slice(0, 3)}-${digits.slice(3)}`;
      } else {
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(
          6,
          10
        )}`;
      }
    } else if (digits.length === 11) {
      // Format as X XXX-XXX-XXXX for 11 digits
      if (digits.length <= 1) {
        return digits;
      } else if (digits.length <= 4) {
        return `${digits.slice(0, 1)} ${digits.slice(1)}`;
      } else if (digits.length <= 7) {
        return `${digits.slice(0, 1)} ${digits.slice(1, 4)}-${digits.slice(4)}`;
      } else {
        return `${digits.slice(0, 1)} ${digits.slice(1, 4)}-${digits.slice(
          4,
          7
        )}-${digits.slice(7, 11)}`;
      }
    } else if (digits.length === 12) {
      // Format as XX XXX-XXX-XXXX for 12 digits
      if (digits.length <= 2) {
        return digits;
      } else if (digits.length <= 5) {
        return `${digits.slice(0, 2)} ${digits.slice(2)}`;
      } else if (digits.length <= 8) {
        return `${digits.slice(0, 2)} ${digits.slice(2, 5)}-${digits.slice(5)}`;
      } else {
        return `${digits.slice(0, 2)} ${digits.slice(2, 5)}-${digits.slice(
          5,
          8
        )}-${digits.slice(8, 12)}`;
      }
    }

    // If more than 12 digits, truncate to 12
    if (digits.length > 12) {
      const truncated = digits.slice(0, 12);
      return `${truncated.slice(0, 2)} ${truncated.slice(
        2,
        5
      )}-${truncated.slice(5, 8)}-${truncated.slice(8, 12)}`;
    }

    return digits;
  };

  // Format mobile for initial load
  const formatMobile = useCallback((input) => {
    if (!input) return "";
    const digits = input.replace(/\D/g, "");
    return formatPhoneForDisplay(digits);
  }, []);

  const fetchCountiesForState = useCallback(
    async (sid) => {
      try {
        const res = await api.get("/auth/counties", {
          params: { maincompanyid: theme.id, stateId: sid },
        });

        const loadedCounties = res.data.data || [];

        setModalCountiesMap((prev) => ({
          ...prev,
          [sid]: loadedCounties,
        }));

        setModalSelectedCounties((prev) => {
          const existing = prev[sid]?.length
            ? prev[sid]
            : savedCountyMap[sid] || [];
          return { ...prev, [sid]: existing };
        });
      } catch (e) {
        console.error(`Failed loading counties for ${sid}`, e);
      }
    },
    [theme.id, savedCountyMap]
  );

  const handleToggleStateCheckbox = useCallback(
    async (sid, checked) => {
      if (checked) {
        setModalStateIds((arr) => (arr.includes(sid) ? arr : [...arr, sid]));
        await fetchCountiesForState(sid);
        setModalSelectedCounties((m) => ({ ...m, [sid]: m[sid] || [] }));
      } else {
        setModalStateIds((arr) => arr.filter((x) => x !== sid));
        setModalSelectedCounties((m) => {
          const copy = { ...m };
          delete copy[sid];
          return copy;
        });
      }
    },
    [fetchCountiesForState]
  );

  const handleToggleCounty = useCallback((sid, cid, checked) => {
    setModalSelectedCounties((m) => {
      const cur = m[sid] ? [...m[sid]] : [];
      if (checked) {
        if (!cur.includes(cid)) cur.push(cid);
      } else {
        const idx = cur.indexOf(cid);
        if (idx >= 0) cur.splice(idx, 1);
      }
      return { ...m, [sid]: cur };
    });
  }, []);

  const handleRemoveCountyTag = useCallback((sid, cid) => {
    setModalSelectedCounties((m) => {
      const cur = (m[sid] || []).filter((x) => x !== cid);
      return { ...m, [sid]: cur };
    });
  }, []);

  // Address state change handler - FIXED: Don't clear county on initial load
  useEffect(() => {
    if (!addressStateValue) {
      setAddressCounties([]);
      return;
    }

    // Track if this is the initial load after form population
    const isInitialLoad =
      !hasInitializedAddressStateRef.current && localUser.address_state;

    // Prevent clearing counties during form submission
    if (isSubmittingRef.current) return;

    api
      .get("/auth/counties", {
        params: { maincompanyid: theme.id, stateId: addressStateValue },
      })
      .then((res) => {
        const newCounties = res.data.data || [];
        setAddressCounties(newCounties);

        // Only clear county if it's not the initial load AND the county doesn't exist in the new list
        if (!isInitialLoad && getValues("address_county")) {
          const currentCountyId = getValues("address_county");
          const countyExists = newCounties.some(
            (c) => (c.id || c.countyid || c.value) === currentCountyId
          );

          if (!countyExists) {
            setValue("address_county", "", { shouldValidate: true });
          }
        }
      })
      .catch((err) => {
        console.error("Error loading address counties", err);
        setAddressCounties([]);
      })
      .finally(() => {
        if (!hasInitializedAddressStateRef.current) {
          hasInitializedAddressStateRef.current = true;
        }
      });
  }, [
    addressStateValue,
    theme.id,
    setValue,
    getValues,
    localUser.address_state,
  ]);

  // Main state change handler - FIXED: Don't clear county on initial load
  useEffect(() => {
    if (!stateFieldValue) {
      setCounties([]);
      return;
    }

    // Track if this is the initial load after form population
    const isInitialLoad = !hasInitializedStateRef.current && localUser.state;

    // Prevent clearing counties during form submission
    if (isSubmittingRef.current) return;

    api
      .get("/auth/counties", {
        params: { maincompanyid: theme.id, stateId: stateFieldValue },
      })
      .then((res) => {
        const newCounties = res.data.data || [];
        setCounties(newCounties);

        // Only clear county if it's not the initial load AND the county doesn't exist in the new list
        if (!isInitialLoad && getValues("county")) {
          const currentCountyId = getValues("county");
          const countyExists = newCounties.some(
            (c) => (c.id || c.countyid || c.value) === currentCountyId
          );

          if (!countyExists) {
            setValue("county", "", { shouldValidate: true });
          }
        }
      })
      .catch((err) => {
        console.error("Error loading counties for state field", err);
        setCounties([]);
      })
      .finally(() => {
        if (!hasInitializedStateRef.current) {
          hasInitializedStateRef.current = true;
        }
      });
  }, [stateFieldValue, theme.id, setValue, getValues, localUser.state]);

  useEffect(() => {
    setValue("states", modalStateIds, { shouldValidate: true });
    setValue("counties", modalSelectedCounties, { shouldValidate: true });
  }, [modalStateIds, modalSelectedCounties, setValue]);

  useEffect(() => {
    if (!modalStateIds.length) return;

    modalStateIds.forEach((sid) => {
      if (!modalCountiesMap[sid]) {
        fetchCountiesForState(sid);
      }
    });
  }, [modalStateIds, modalCountiesMap, fetchCountiesForState]);

  useEffect(() => {
    if (selectedCountyId && counties.length) {
      setValue("county", selectedCountyId, { shouldValidate: true });
    }
  }, [counties, selectedCountyId, setValue]);

  /** ✅ Load user profile on mount */
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/get-user-profile");
        if (res.data?.status === "success" && res.data?.data) {
          const u = res.data.data;
          const full = res.data.salesRep;

          const apiUser = res.data?.data || {};
          const salesRep = res.data?.salesRep || {};

          const formattedUser = {
            ...apiUser,
            first_name: apiUser.first_name || "",
            last_name: apiUser.last_name || "",
            email: apiUser.email || "",
            phone: apiUser.phone ? formatMobile(apiUser.phone) : "",
            title: salesRep.title || apiUser.title || "",
            state: apiUser.state || "",
            county: apiUser.county || "",
            postal_code: apiUser.postal_code || "",
            address1: salesRep.address1 || "",
            address2: salesRep.address2 || "",
            address_city: salesRep.address_city || "",
            address_state: salesRep.address_state || "",
            address_county: salesRep.address_county || "",
            address_postal_code: salesRep.address_zip || "",
          };

          setLocalUser(formattedUser);
          reset(formattedUser);

          if (u.user_type === "S" || u.user_type === "SM") {
            setValue("title", full.title || "", { shouldValidate: true });
            setValue("address_state", full.address_state || "", {
              shouldValidate: true,
            });
            setValue("address_county", full.address_county || "", {
              shouldValidate: true,
            });

            const sel = Array.isArray(full.selected_locations)
              ? full.selected_locations
              : [];
            if (sel.length) {
              const stateIds = [];
              const tmpCountyMap = {};

              for (const s of sel) {
                const sid = s.state_id || s.stateid || s.id;
                if (!sid) continue;

                if (!stateIds.includes(sid)) stateIds.push(sid);

                const counties = Array.isArray(s.counties) ? s.counties : [];
                tmpCountyMap[sid] = counties
                  .map((c) => c.county_id || c.countyid || c.id)
                  .filter(Boolean);
              }

              setModalStateIds(stateIds);
              setSavedCountyMap(tmpCountyMap);
            }
          }

          if (u.profile_pic) {
            setProfilePic(
              `${process.env.REACT_APP_ASSETS_BASE_URL}/uploads/users/${
                u.profile_pic
              }?t=${Date.now()}`
            );
          } else {
            setProfilePic(profilepics);
          }

          localStorage.setItem("user", JSON.stringify(u));
        }
      } catch (err) {
        console.error("Error fetching user profile", err);
      } finally {
        setIsProfileLoaded(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** ✅ Cleanup blob URL on unmount */
  useEffect(() => {
    return () => {
      if (previewPic) URL.revokeObjectURL(previewPic);
    };
  }, [previewPic]);

  /** ✅ Dropdowns */
  useEffect(() => {
    api
      .get(`/auth/states`, { params: { maincompanyid: theme.id } })
      .then((res) => setStates(res.data.data || []));
  }, [theme.id]);

  useEffect(() => {
    if (!localUser.state) return setCounties([]);
    api
      .get(`/auth/counties`, {
        params: { maincompanyid: theme.id, stateId: localUser.state },
      })
      .then((res) => setCounties(res.data.data || []));
  }, [localUser.state, theme.id]);

  useEffect(() => {
    if (!localUser.state || !localUser.county) return setTitleReps([]);

    api
      .get(`/auth/titlereps`, {
        params: {
          maincompanyid: theme.id,
          stateId: localUser.state,
          countyId: localUser.county,
        },
      })
      .then((res) => setTitleReps(res.data.data || []));
  }, [localUser.state, localUser.county, theme.id]);

  /** ✅ Keep form synced with loaded user */
  useEffect(() => {
    if (isProfileLoaded) {
      reset(localUser);
    }
  }, [localUser, reset, isProfileLoaded]);

  // Trigger validation on form changes
  useEffect(() => {
    const subscription = watch(() => {
      trigger();
    });
    return () => subscription.unsubscribe();
  }, [watch, trigger]);

  /** ✅ Handle profile image upload */
  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    const previewURL = URL.createObjectURL(file);
    setPreviewPic(previewURL);
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append("profile_image", file);

      const res = await api.put("/upload-profile-picture", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data?.status === "success") {
        const filename =
          res.data.fileName ||
          res.data.profile_pic ||
          res.data.fileUrl ||
          res.data.filePath;

        const fullUrl = `${
          process.env.REACT_APP_ASSETS_BASE_URL
        }/uploads/users/${filename}?t=${Date.now()}`;
        setTimeout(() => {
          setProfilePic(fullUrl);
          setPreviewPic(null);
        }, 500);

        const updatedUser = { ...user, profile_pic: filename };
        setLocalUser((prev) => ({ ...prev, profile_pic: filename }));
        updateUser(updatedUser);
        setToaster({
          message: "Profile picture updated successfully!",
          type: "success",
          visible: true,
        });
      } else {
        setToaster({
          message: "Failed to updated profile picture!",
          type: "error",
          visible: true,
        });
      }
    } catch (err) {
      console.error("Upload error:", err);
      alert("Error uploading profile picture.");
    } finally {
      setIsUploading(false);
      setTimeout(() => {
        setToaster((prev) => ({ ...prev, visible: false }));
      }, 2000);
    }
  };

  const handlePostalBlur = async (eOrValue) => {
    const zip =
      typeof eOrValue === "string" ? eOrValue : eOrValue.target?.value;
    if (!zip) return;

    if (zipControllerRef.current) {
      try {
        zipControllerRef.current.abort();
      } catch {}
    }

    const controller = new AbortController();
    zipControllerRef.current = controller;

    const stateid = getValues("state");
    const countyid = getValues("county") || selectedCountyId;

    try {
      const res = await api.post(
        "/auth/verify-zip",
        { zipcode: zip, maincompanyid: theme.id, stateid, countyid },
        { signal: controller.signal }
      );
      if (res.data?.status === "fail") {
        setError("postal_code", {
          type: "manual",
          message: trans("register_zip_invalid"),
        });
      } else {
        const state = res.data?.data?.stateid;
        const county = res.data?.data?.countyid;
        setValue("state", "");
        setTimeout(() => {
          setValue("state", state, { shouldValidate: true });
          setSelectedCountyId(county);
          clearErrors("postal_code");
        }, 100);
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        setError("postal_code", {
          type: "manual",
          message: trans("register_zip_validation_failed"),
        });
      }
    }
  };

  /** ✅ Handle profile form save - FIXED: Preserve county values */
  const onSubmit = async (data) => {
    if (!isEditing) {
      return;
    }

    // Set submitting flag to prevent county clearing
    isSubmittingRef.current = true;

    // Check for any validation errors
    const hasErrors = Object.keys(errors).length > 0;
    if (hasErrors) {
      isSubmittingRef.current = false;
      setToaster({
        message: "Please fix all errors before saving",
        type: "error",
        visible: true,
      });
      setTimeout(
        () => setToaster((prev) => ({ ...prev, visible: false })),
        4000
      );
      return;
    }

    // Additional phone validation - UPDATED for 10-12 digits
    const phoneDigits = data.phone ? data.phone.replace(/\D/g, "") : "";
    if (phoneDigits.length < 10 || phoneDigits.length > 12) {
      isSubmittingRef.current = false;
      setError("phone", {
        type: "manual",
        message:
          trans("validation_phone_invalid") ||
          "Phone number must be 10-12 digits",
      });
      setToaster({
        message: "Please enter a valid 10-12 digit phone number",
        type: "error",
        visible: true,
      });
      setTimeout(
        () => setToaster((prev) => ({ ...prev, visible: false })),
        4000
      );
      return;
    }

    // Validate county fields are not empty after state changes
    if (data.state && !data.county) {
      isSubmittingRef.current = false;
      setError("county", {
        type: "manual",
        message: trans("validation_county_required") || "County is required",
      });
      return;
    }

    if (data.address_state && !data.address_county) {
      isSubmittingRef.current = false;
      setError("address_county", {
        type: "manual",
        message: trans("validation_county_required") || "County is required",
      });
      return;
    }

    try {
      const { selected_locations } = buildSelectedLocations({
        modalStateIds,
        modalSelectedCounties,
        modalCountiesMap,
        states,
      });

      const phoneForSubmission = data.phone
        ? data.phone.replace(/\D/g, "")
        : "";

      // Save current county values before API call
      const currentCounty = data.county;
      const currentAddressCounty = data.address_county;

      const payload = {
        firstname: data.first_name || "",
        lastname: data.last_name || "",
        phone: phoneForSubmission,
        title: data.title || "",
        maincompanyid: theme.id || null,
        stateid: data.state || null,
        countyid: data.county || null,
        postal_code: data.postal_code || "",
        address1: data.address1 || "",
        address2: data.address2 || "",
        address_city: data.address_city || "",
        address_state: data.address_state || null,
        address_county: data.address_county || null,
        address_zip: data.address_postal_code || "",
        selected_locations,
      };

      const res = await api.put(
        `/users/sales-rep/${theme.id}/${data.titlerep}`,
        payload
      );

      if (res.data?.status === "success") {
        // Update localUser with all form data including counties
        const updatedUser = {
          ...localUser,
          ...data,
          phone: data.phone,
          county: currentCounty, // Explicitly preserve county value
          address_county: currentAddressCounty, // Explicitly preserve address county value
        };

        // Update localUser state
        setLocalUser(updatedUser);

        // Update form with the saved data
        reset(updatedUser);

        // If API returns updated user data, use it
        if (res.data.datauser) {
          updateUser(res.data.datauser);
        } else {
          // Otherwise update with our local data
          updateUser(updatedUser);
        }

        setToaster({
          message: "Profile updated successfully!",
          type: "success",
          visible: true,
        });

        setTimeout(() => {
          setToaster((prev) => ({ ...prev, visible: false }));
        }, 2000);

        setIsEditing(false);
      } else {
        // If API fails but we want to keep form data
        const updatedUser = {
          ...localUser,
          ...data,
          phone: data.phone,
          county: currentCounty,
          address_county: currentAddressCounty,
        };
        setLocalUser(updatedUser);
        reset(updatedUser);

        setToaster({
          message: "Profile saved successfully!",
          type: "success",
          visible: true,
        });

        setTimeout(() => {
          setToaster((prev) => ({ ...prev, visible: false }));
        }, 2000);

        setIsEditing(false);
      }
    } catch (err) {
      console.warn("Failed updating titlerep for sales profile", err);

      // Even on error, preserve the county values in local state
      const currentCounty = getValues("county");
      const currentAddressCounty = getValues("address_county");

      const updatedUser = {
        ...localUser,
        ...data,
        phone: data.phone,
        county: currentCounty,
        address_county: currentAddressCounty,
      };
      setLocalUser(updatedUser);
      // Use reset with shouldKeepDirtyValues to preserve current form state
      reset(updatedUser, {
        keepValues: true, // This preserves current form values
        keepDirty: true,
        keepErrors: false,
        keepIsValid: false,
      });

      setToaster({
        message: "Profile saved locally but API call failed",
        type: "error",
        visible: true,
      });
      setTimeout(
        () => setToaster((prev) => ({ ...prev, visible: false })),
        4000
      );
    } finally {
      // Reset the submitting flag after a short delay
      setTimeout(() => {
        isSubmittingRef.current = false;
      }, 1000);
    }
  };

  const handleCheckAllCounties = (sid, counties) => {
    const ids = counties.map((c) => c.id || c.countyid || c.value);
    setModalSelectedCounties((prev) => ({
      ...prev,
      [sid]: ids,
    }));
  };

  const handleUncheckAllCounties = (sid) => {
    setModalSelectedCounties((prev) => ({
      ...prev,
      [sid]: [],
    }));
  };

  /** ✅ Change password handler */
  const onPasswordChange = async (e) => {
    e.preventDefault();
    if (!passwords.password || !passwords.confirmPassword) {
      return alert("Please fill out both fields.");
    }
    if (passwords.password !== passwords.confirmPassword) {
      return alert("Passwords do not match!");
    }
    try {
      const res = await api.post("/users/change-password", {
        password: passwords.password,
      });
      if (res.data?.status === "success") {
        setToaster({
          message: "Password updated successfully, Redirect to Login.",
          type: "success",
          visible: true,
        });
        setTimeout(async () => {
          setToaster((prev) => ({ ...prev, visible: false }));
          await logoutUser();
          navigate(buildCompanyPath(""));
        }, 2000);
        setPasswords({ password: "", confirmPassword: "" });
      } else {
        alert("Error changing password.");
      }
    } catch (err) {
      alert("Error changing password.");
    }
  };

  const handleImageSelect = () => {
    document.getElementById("imgInput").click();
  };

  // Handle discard button - preserve county values
  const handleDiscard = () => {
    // Get current state and county values before reset
    const currentState = getValues("state");
    const currentCounty = getValues("county");
    const currentAddressState = getValues("address_state");
    const currentAddressCounty = getValues("address_county");

    reset(localUser);

    // Restore county values if states haven't changed
    if (currentState === localUser.state && currentCounty) {
      setValue("county", currentCounty, { shouldValidate: true });
    }

    if (
      currentAddressState === localUser.address_state &&
      currentAddressCounty
    ) {
      setValue("address_county", currentAddressCounty, {
        shouldValidate: true,
      });
    }

    setIsEditing(false);
  };

  // Check if form has any errors (excluding undefined)
  const hasFormErrors = () => {
    return Object.keys(errors).length > 0;
  };

  return (
    <>
      <div className="user-p">
        <div className="user-form">
          <div className="user-upload-img">
            <div className="user-info">
              <div className="user-p-pics">
                <div className={`uploader ${isUploading ? "uploading" : ""}`}>
                  <input
                    id="imgInput"
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleImageChange}
                  />
                  <label
                    htmlFor="imgInput"
                    className="upload-box"
                    aria-label="Upload image"
                  >
                    {!isUploading && (
                      <div className="upload-hint">
                        <svg
                          viewBox="0 0 24 24"
                          width="18"
                          height="18"
                          aria-hidden="true"
                        >
                          <path
                            d="M12 16v-8m0 0l-3 3m3-3l3 3M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span>Upload</span>
                      </div>
                    )}
                    <img
                      id="preview"
                      src={
                        previewPic ||
                        (isProfileLoaded ? profilePic : profilepics)
                      }
                      alt="Profile"
                      style={{
                        display: "block",
                        objectFit: "cover",
                        borderRadius: "10px",
                      }}
                      onError={(e) => (e.target.src = profilepics)}
                    />

                    {isUploading && (
                      <div className="overlay">
                        <div className="spinner"></div>
                      </div>
                    )}
                  </label>
                  <button className="edit-up-img" onClick={handleImageSelect}>
                    Click here to change the image
                  </button>
                </div>
              </div>

              <div className="user-p-name">
                <h3>
                  Welcome,{" "}
                  {ucWords(`${localUser.first_name} ${localUser.last_name}`)}
                </h3>
              </div>
              <div className="user-p-desc">
                <p>
                  Click on image to change. This image will appear on your net
                  sheets.
                </p>
              </div>
            </div>
          </div>

          <div className="reg-f-container">
            <form onSubmit={handleSubmit(onSubmit)}>
              {/* FirstName & lastName */}
              <div className="frm-field-2">
                <div className="form-group">
                  <FloatingInput
                    label="First Name"
                    disabled={!isEditing}
                    {...register("first_name")}
                  />
                  {errors.first_name && (
                    <p className="field-error">{errors.first_name.message}</p>
                  )}
                </div>
                <div className="form-group">
                  <FloatingInput
                    label="Last Name"
                    disabled={!isEditing}
                    {...register("last_name")}
                  />
                  {errors.last_name && (
                    <p className="field-error">{errors.last_name.message}</p>
                  )}
                </div>
              </div>

              {/* Email & phone */}
              <div className="frm-field-2">
                <div className="form-group">
                  <FloatingInput
                    label="Email"
                    disabled
                    {...register("email")}
                  />
                </div>
                <div className="form-group">
                  <FloatingInput
                    label="Title"
                    disabled={!isEditing}
                    {...register("title")}
                  />
                  {errors.usertitle && (
                    <p className="field-error">{errors.usertitle.message}</p>
                  )}
                </div>
              </div>

              {/* State & county */}
              <div className="frm-field-2">
                <div className="form-group">
                  <Controller
                    name="state"
                    control={control}                    
                    render={({ field }) => {
                      const options = (states || []).map((s) => ({
                        value: s.id,
                        label: s.statename,
                      }));

                      const selectedOption =
                        options.find(
                          (opt) => Number(opt.value) === Number(field.value)
                        ) || null;

                      return (
                        <CustomSelect
                          options={options}
                          label={"State"}
                          placeholder={selectedOption?.label || "Select State"}
                          value={selectedOption}
                          onChange={(val) => {
                            field.onChange(val);
                            // Don't clear county immediately, let the useEffect handle it
                          }}
                          disabled={!isEditing}
                        />
                      );
                    }}
                  />
                  {errors.state && (
                    <p className="field-error">{errors.state.message}</p>
                  )}
                </div>
                <div className="form-group">
                  <Controller
                    name="county"
                    control={control}
                    render={({ field }) => {
                      const options = (counties || []).map((c) => ({
                        value: c.id,
                        label: c.countyname,
                      }));

                      const selectedOption =
                        options.find(
                          (opt) => Number(opt.value) === Number(field.value)
                        ) || null;

                      return (
                        <CustomSelect
                          options={options}
                          label={"County"}
                          placeholder={selectedOption?.label || "Select County"}
                          value={selectedOption}
                          onChange={(val) => field.onChange(val)}
                          disabled={!isEditing}
                          isClearable={false}
                        />
                      );
                    }}
                  />
                  {errors.county && (
                    <p className="field-error">{errors.county.message}</p>
                  )}
                </div>
              </div>

              {/* ZipCode & Phone */}
              <div className="frm-field-2">
                <div className="form-group">
                  <FloatingInput
                    label="ZIP Code"
                    disabled={!isEditing}
                    {...register("postal_code")}
                    onBlur={handlePostalBlur}
                    maxLength={5}
                  />
                  {errors.postal_code && (
                    <p className="field-error">{errors.postal_code.message}</p>
                  )}
                </div>
                <div className="form-group">
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field, fieldState }) => (
                      <div>
                        <FloatingInput
                          label="Phone Number"
                          value={field.value || ""}
                          disabled={!isEditing}
                          onChange={(e) => {
                            // Only allow digits, spaces, and dashes
                            const value = e.target.value;
                            const cleaned = value.replace(/[^\d\s-]/g, "");

                            // Format based on length
                            const formatted = formatPhoneInput(cleaned);

                            field.onChange(formatted);
                            trigger("phone");
                          }}
                          onBlur={() => {
                            field.onBlur();
                            trigger("phone");
                          }}
                          ref={field.ref}
                          placeholder="Enter 10 or 12 digits"
                          maxLength={20} // Fixed high value to allow typing
                        />
                        {fieldState.error && (
                          <p className="field-error">
                            {fieldState.error.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>

              {/* STATE & COUNTY SELECTION */}
              <div className="state-county-wrapper">
                <div className="section-label">
                  Select a state to choose counties
                </div>
                <div className="state-county-grid">
                  <div className="state-list">
                    {states.map((s) => {
                      const sid = s.id || s.stateid || s.value;
                      const checked = modalStateIds.includes(sid);

                      return (
                        <label key={sid} className="state-item">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!isEditing}
                            onChange={(e) =>
                              handleToggleStateCheckbox(sid, e.target.checked)
                            }
                          />
                          <span>{s.statename || s.name}</span>
                        </label>
                      );
                    })}
                  </div>

                  <div className="county-panel-container">
                    {modalStateIds.map((sid) => {
                      const sObj = states.find(
                        (s) => (s.id || s.stateid || s.value) === sid
                      ) || {
                        id: sid,
                      };

                      const counties = modalCountiesMap[sid] || [];
                      const selectedForState = modalSelectedCounties[sid] || [];

                      return (
                        <div key={sid} className="county-panel">
                          <div className="county-panel-header">
                            <span className="county-panel-title">
                              {sObj.statename || sObj.name || `State ${sid}`}
                            </span>
                            <span className="county-selected-count">
                              {selectedForState.length} selected
                            </span>
                          </div>

                          <div className="county-actions">
                            <button
                              type="button"
                              className="check-all-btn"
                              disabled={!isEditing}
                              onClick={() =>
                                handleCheckAllCounties(sid, counties)
                              }
                            >
                              Check All
                            </button>

                            <button
                              type="button"
                              className="uncheck-all-btn"
                              disabled={!isEditing}
                              onClick={() => handleUncheckAllCounties(sid)}
                            >
                              Uncheck All
                            </button>
                          </div>

                          <div className="county-list">
                            {counties.map((c) => {
                              const cid = c.id || c.countyid || c.value;
                              const checked = selectedForState.includes(cid);

                              return (
                                <label key={cid} className="county-item">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    disabled={!isEditing}
                                    onChange={(e) =>
                                      handleToggleCounty(
                                        sid,
                                        cid,
                                        e.target.checked
                                      )
                                    }
                                  />
                                  <span>{c.countyname || c.name}</span>
                                </label>
                              );
                            })}
                          </div>

                          <div className="county-tags">
                            {selectedForState.map((cid) => {
                              const cObj = (modalCountiesMap[sid] || []).find(
                                (c) => (c.id || c.countyid || c.value) === cid
                              ) || { id: cid };

                              return (
                                <div key={cid} className="county-tag">
                                  <span>
                                    {cObj.countyname ||
                                      cObj.name ||
                                      `County ${cid}`}
                                  </span>
                                  <button
                                    type="button"
                                    disabled={!isEditing}
                                    onClick={() =>
                                      handleRemoveCountyTag(sid, cid)
                                    }
                                  >
                                    ×
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="frm-field-2 lbl-select-state">
                Sales Rep Address
              </div>

              {/* Address & city */}
              <div className="frm-field-2">
                <div className="form-group">
                  <FloatingInput
                    label="Mailing Address 1"
                    disabled={!isEditing}
                    {...register("address1")}
                  />
                  {errors.mailing_address && (
                    <p className="field-error">
                      {errors.mailing_address.message}
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <FloatingInput
                    label="Mailing Address 2"
                    disabled={!isEditing}
                    {...register("address2")}
                  />
                </div>
                <div className="form-group">
                  {errors.city && (
                    <p className="field-error">{errors.city.message}</p>
                  )}
                  <FloatingInput
                    label="City"
                    disabled={!isEditing}
                    {...register("address_city")}
                  />
                </div>
              </div>

              {/* State & county */}
              <div className="frm-field-2">
                <div className="form-group">
                  <Controller
                    name="address_state"
                    control={control}
                    render={({ field }) => {
                      const options = (states || []).map((s) => ({
                        value: s.id,
                        label: s.statename,
                      }));

                      const selectedOption =
                        options.find(
                          (opt) => Number(opt.value) === Number(field.value)
                        ) || null;

                      return (
                        <CustomSelect
                          options={options}
                          label={"State"}
                          placeholder={selectedOption?.label || "Select State"}
                          value={selectedOption}
                          onChange={(val) => {
                            field.onChange(val);
                            // Don't clear county immediately, let the useEffect handle it
                          }}
                          disabled={!isEditing}
                        />
                      );
                    }}
                  />
                  {errors.address_state && (
                    <p className="field-error">
                      {errors.address_state.message}
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <Controller
                    name="address_county"
                    control={control}
                    render={({ field }) => {
                      const options = (addressCounties || []).map((c) => ({
                        value: c.id,
                        label: c.countyname,
                      }));

                      const selectedOption =
                        options.find(
                          (opt) => Number(opt.value) === Number(field.value)
                        ) || null;

                      return (
                        <CustomSelect
                          options={options}
                          label={"County"}
                          placeholder={selectedOption?.label || "Select County"}
                          value={selectedOption}
                          onChange={(val) => field.onChange(val)}
                          disabled={!isEditing}
                          isClearable={false}
                        />
                      );
                    }}
                  />
                  {errors.address_county && (
                    <p className="field-error">
                      {errors.address_county.message}
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <FloatingInput
                    label="ZIP Code"
                    disabled={!isEditing}
                    {...register("address_postal_code")}
                  />
                </div>
              </div>

              {/* ✅ Form Buttons */}
              <div className="full-width-container-btn">
                <div className="left-btn-container">
                  {isEditing && (
                    <button
                      type="button"
                      onClick={handleDiscard}
                      className="submit-btn btn-bg inpt-btn w-120 discard-btn"
                    >
                      Discard
                    </button>
                  )}
                </div>
                <div className="right-btn-container">
                  {isEditing && (
                    <button
                      type="submit"
                      disabled={isSubmitting || hasFormErrors()}
                      className="submit-btn btn-bg inpt-btn w-190"
                      style={{
                        backgroundColor: hasFormErrors()
                          ? "#ccc"
                          : theme.primary_color,
                        cursor: hasFormErrors() ? "not-allowed" : "pointer",
                      }}
                    >
                      {isSubmitting ? "Saving..." : "Save Changes"}
                    </button>
                  )}
                </div>
              </div>
            </form>

            {!isEditing && (
              <div className="full-width-container-btn">
                <div className="right-btn-container">
                  <button
                    type="button"
                    onClick={() => setIsEditing(true)}
                    className="submit-btn btn-bg inpt-btn w-190"
                    style={{ backgroundColor: theme.primary_color }}
                  >
                    Update my profile
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ✅ Change Password */}
        <div className="user-form change-password-container">
          <div className="user-upload-img"></div>
          <div className="reg-f-containe">
            <div className="user-info top-info-line">
              <div className="user-p-name">
                <h3>Change your password</h3>
              </div>
              <div className="user-p-desc">
                <p>
                  For security reasons, please change your password every 3
                  months.
                </p>
              </div>
              <div className="user-form">
                <form onSubmit={onPasswordChange} className="reg-f-container">
                  <div className="frm-field-2">
                    <div className="form-group">
                      <FloatingInput
                        label="New password"
                        type="password"
                        value={passwords.password}
                        onChange={(e) =>
                          setPasswords((prev) => ({
                            ...prev,
                            password: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="form-group">
                      <FloatingInput
                        label="Confirm password"
                        type="password"
                        value={passwords.confirmPassword}
                        onChange={(e) =>
                          setPasswords((prev) => ({
                            ...prev,
                            confirmPassword: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>

                  <div className="full-width-container-btn">
                    <div className="right-btn-container">
                      <button
                        type="submit"
                        className="submit-btn btn-bg inpt-btn w-190"
                        style={{ backgroundColor: theme.primary_color }}
                      >
                        Update my password
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <Toast
          message={toaster.message}
          type={toaster.type}
          visible={toaster.visible}
        />
      </div>
    </>
  );
}
